import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import ReactDOM from 'react-dom/client';
import ConfirmModal from '../Common/ConfirmModal';
import listService from 'ListService';

const MODAL_TARGET_ID = 'common-modal-dialog';

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width:100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 99998;
    cursor: default!important;   
    user-select: none;
    z-index: 99999999;
`;

const Modal = styled.div`
    position: fixed;
    background: white;
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    top:50%;
    left:50%;
    transform: translate(-50%,-50%);
    cursor: default!important;
    border-radius: 20px;
    overflow: hidden;
    font-family: 'Poppins', sans-serif;
   
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #85B33E;
  padding: 12px 20px;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  color: white;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.5rem; 
  font-weight: 400;
  color: white; 
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white; 
  font-size: 1.5rem; 
  font-weight: bold;
  cursor: pointer;
  padding: 1px 5px;
  line-height: 1;
  outline: 1px solid rgba(255, 255, 255, 0.5);
  outline-offset: 1px;
  border-radius: 50%;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: 2px solid rgba(255, 255, 255, 0.5); 
    outline-offset: 2px;
  }
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    overflow: hidden; /* prevent overflow from panels/buttons */
    padding: 20px;
      height: calc(100% - 20px);
    `;

function CommonModal({ isOpen, onClose, headerTitle, children }) {
    const [isDirty, setIsDirty] = useState(false);
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const childRef = useRef();

    const forceClose = () => {
        listService.isUndoRedoEnabled = true;
        if (onClose) {
            onClose();
        }
    };

    const handleClose = (options = {}) => {
        const { skipDirtyCheck = false } = options;

        if (isDirty && !skipDirtyCheck) {
            setShowConfirmClose(true);
        } else {
            forceClose();
        }
    };

    const handleSaveAndClose = async () => {
        setShowConfirmClose(false);
        const saveSuccess = await childRef.current?.save?.();
        if (saveSuccess) {
            forceClose();
            return; 
        }       
    };

    listService.isUndoRedoEnabled = false;
    
    if (!isOpen) {
        return null;
    }

    return (
        <>
            <Overlay className="react-modal-container">
                <Modal>
                    <ModalHeader>
                        <Title>{headerTitle}</Title>
                        <CloseButton onClick={handleClose} aria-label="Close modal">&times;</CloseButton>
                    </ModalHeader>
                    <ModalContent>
                        {React.cloneElement(children, { setIsDirty, ref: childRef, requestClose: handleClose })}
                    </ModalContent>
                </Modal>
            </Overlay>

            {showConfirmClose && (
                <ConfirmModal
                    title="Unsaved Changes"
                    onClose={() => setShowConfirmClose(false)}
                    buttons={[
                        { label: 'Cancel', variant: 'gray', onClick: () => setShowConfirmClose(false) },
                        { label: "Don't Save", variant: 'default', onClick: forceClose },
                        { label: 'Save and Close', variant: 'orange', onClick: handleSaveAndClose }
                    ]}
                >
                    <div>You have unsaved changes. Would you like to save them before closing?</div>
                </ConfirmModal>
            )}
        </>
    );

}

const ModalHost = ({ Component, componentProps, modalProps, onClose }) => (
    <CommonModal isOpen={true} onClose={onClose} {...modalProps}>
        <Component {...componentProps} />
    </CommonModal>
);

const close = () => {
    const targetElement = document.getElementById(MODAL_TARGET_ID);
    if (targetElement) {
        const root = targetElement._reactRoot;
        if (root) {
            root.unmount();
            delete targetElement._reactRoot; 
        }
        targetElement.remove();
    }
};

const open = (Component, props = {}) => {
    const { modalProps = {}, componentProps = {} } = props;
    let targetElement = document.getElementById(MODAL_TARGET_ID);

    if (!targetElement) {
        targetElement = document.createElement('div');
        targetElement.id = MODAL_TARGET_ID;
        document.body.appendChild(targetElement);
    }

    const uniqueKey = Date.now();

    let root = targetElement._reactRoot;

    if (!root) {
        root = ReactDOM.createRoot(targetElement);
        targetElement._reactRoot = root; 
    }

    root.render(
        <ModalHost
            Component={Component}
            modalProps={modalProps}
            componentProps={{ ...componentProps, key: uniqueKey }}
            onClose={close}
        />
    );
};

CommonModal.open = open;
CommonModal.close = close;

export default CommonModal;